import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { message } from 'antd';
import { type TFunction } from 'i18next';
import { type Feature } from 'ol';
import type Map from 'ol/Map';
import type VectorSource from 'ol/source/Vector';
import { type MutableRefObject, useEffect, useRef } from 'react';
import { Subject } from 'rxjs';
import { v4 } from 'uuid';
import { type MapGeometryItem } from '../createMapSchema.ts';
import { normalizeGeometryType } from '../geometryTypeUtils.ts';
import { type IMapWidget, MapGeometryType } from '../IMapWidget.ts';
import { type MapSubjectEvent } from '../MapWidgetComponent.tsx';
import { geoJSONEpsg3857 } from '../mapStyleUtils.ts';
import {
  convertGeometryItemsToFeatures,
  fitViewToFeatures,
} from './convertGeometryItemsToFeatures.ts';
import { getRoundedFeatureArea } from './intersection/utils/getRoundedFeatureArea.ts';
import { getRoundedFeatureLength } from './intersection/utils/getRoundedFeatureLength.ts';

interface IUseMapSubjectHandlerOptions {
  editor: RelationEditor<MapGeometryItem>;
  featuresSource: MutableRefObject<VectorSource>;
  widget: IMapWidget;
  mapRef: MutableRefObject<Map | null>;
  getNextIndex: () => number;
  getNextIndexBatch: (count: number) => number[];
  triggerIntersections: (feature?: Feature) => void;
  resetIntersectionEditors: () => void;
  t: TFunction;
}

/** Serializza una Feature OL in un oggetto `MapGeometryItem` persistibile. */
function encodeFeature(
  feature: Feature,
  geometryIndex: number,
): MapGeometryItem {
  const geometryType = normalizeGeometryType(feature.getGeometry()?.getType());

  return {
    feature: geoJSONEpsg3857.writeFeature(feature),
    id: feature.getId() as string,
    // Area e lunghezza sono pertinenti solo rispettivamente a poligoni e
    // linee: le calcoliamo solo per il tipo corrispondente.
    area:
      geometryType === MapGeometryType.Polygon
        ? getRoundedFeatureArea(feature)
        : undefined,
    length:
      geometryType === MapGeometryType.LineString
        ? getRoundedFeatureLength(feature)
        : undefined,
    geometryType,
    geometryIndex,
  };
}

/** Aggiunge una nuova geometria disegnata o da particella catastale. In selezione singola sostituisce le esistenti. */
function handleAdd(
  event: Extract<MapSubjectEvent, { type: 'add' }>,
  options: IUseMapSubjectHandlerOptions,
) {
  const { featuresSource, editor, widget, getNextIndex, triggerIntersections } =
    options;
  const feature = event.feature;
  feature.setId(v4());
  if (!feature.getGeometry()) return;

  const nextIndex = getNextIndex();
  feature.set('geometryIndex', nextIndex);

  // Utilizzato per le particelle catastali
  if (event.addToSource) {
    featuresSource.current.addFeature(feature);
  }

  // Per la selezione singola, rimuovo tutte le geometrie tranne quella
  // selezionata attualmente.
  if (widget.selection?.multiple === false) {
    featuresSource.current.removeFeatures(
      featuresSource.current.getFeatures().filter(f => f !== feature),
    );
    editor.replace([encodeFeature(feature, nextIndex)]);
  } else {
    editor.append(encodeFeature(feature, nextIndex));
  }

  triggerIntersections(feature);
}

/** Carica geometrie da file: resetta le esistenti e centra la mappa sull'extent. */
function handleUpload(
  event: Extract<MapSubjectEvent, { type: 'upload' }>,
  options: IUseMapSubjectHandlerOptions,
) {
  const {
    featuresSource,
    editor,
    mapRef,
    getNextIndexBatch,
    triggerIntersections,
    t,
  } = options;
  const features = event.features;
  if (features.length === 0) {
    message.warning(
      t(
        'map-widget.upload-warning-message',
        'Nessuna geometria presente nel file.',
      ),
    );
    return;
  }

  const indexes = getNextIndexBatch(features.length);
  const items = features.map((feature, i) => {
    feature.setId(v4());
    feature.set('geometryIndex', indexes[i]);
    return encodeFeature(feature, indexes[i]);
  });

  // Resetta le geometrie sulla mappa prima di aggiungere quelle caricate
  featuresSource.current.clear();

  // Essendo un caricamento da file, non sono presenti ancora
  // nel layer delle geometrie disegnate
  featuresSource.current.addFeatures(features);

  editor.replace(items);
  triggerIntersections();

  mapRef.current?.getView().fit(featuresSource.current.getExtent(), {
    padding: [64, 64, 64, 64],
    duration: 400,
    maxZoom: 19,
  });
}

/** Aggiorna una geometria esistente dopo modifica sulla mappa. Gestisce retrocompatibilità per `geometryIndex` mancante. */
function handleModify(
  event: Extract<MapSubjectEvent, { type: 'modify' }>,
  options: IUseMapSubjectHandlerOptions,
) {
  const { editor, getNextIndex, triggerIntersections } = options;
  const feature = event.feature;
  const items = editor.getValue();
  const editorIndex = items.findIndex(i => i.id === feature.getId());
  if (editorIndex === -1) {
    // biome-ignore format: <tbd>
    console.warn(`[MapWidgetComponent] Feature non trovata nell'editor`, { feature, id: feature.getId() });
    return;
  }

  // Geometrie salvate prima dell'introduzione di `geometryIndex` non hanno
  // questo campo: cerchiamo nell'item, poi nella feature OL, infine ne
  // allochiamo uno nuovo col counter per evitare duplicati (fallback a 0).
  const existingGeometryIndex =
    items[editorIndex].geometryIndex ??
    (feature.get('geometryIndex') as number | undefined);
  const geometryIndex = existingGeometryIndex ?? getNextIndex();
  if (existingGeometryIndex == null) {
    feature.set('geometryIndex', geometryIndex);
  }
  editor.updateAt(editorIndex, encodeFeature(feature, geometryIndex));
  triggerIntersections(feature);
}

/** Rimuove una geometria dal layer e dall'editor tramite riferimento Feature. */
function handleRemove(
  event: Extract<MapSubjectEvent, { type: 'remove' }>,
  options: IUseMapSubjectHandlerOptions,
) {
  const { featuresSource, editor, triggerIntersections } = options;
  const feature = event.feature;
  const editorIndex = editor
    .getValue()
    .findIndex(i => i.id === feature.getId());
  if (editorIndex === -1) {
    // biome-ignore format: <tbd>
    console.warn(`[MapWidgetComponent] Feature non trovata nell'editor`, { feature, id: feature.getId() });
    return;
  }

  featuresSource.current.removeFeature(feature);
  editor.removeAt(editorIndex);
  triggerIntersections();
}

/** Rimuove una geometria dal layer e dall'editor tramite id stringa (usato dalla tabella geometrie). */
function handleRemoveId(
  event: Extract<MapSubjectEvent, { type: 'removeId' }>,
  options: IUseMapSubjectHandlerOptions,
) {
  const { featuresSource, editor, triggerIntersections } = options;
  const editorIndex = editor.getValue().findIndex(i => i.id === event.id);
  if (editorIndex === -1) {
    // biome-ignore format: <tbd>
    console.warn(`[MapWidgetComponent] Feature non trovata nell'editor`, { id: event.id });
    return;
  }
  editor.removeAt(editorIndex);

  const feature = featuresSource.current.getFeatureById(event.id);
  if (!feature) {
    // biome-ignore format: <tbd>
    console.warn(`[MapWidgetComponent] Feature non trovata nella mappa`, { id: event.id });
    return;
  }
  featuresSource.current.removeFeature(feature);

  triggerIntersections();
}

/** Resetta tutte le geometrie disegnate e le aree intersecate (catastali + custom). */
function handleReset(options: IUseMapSubjectHandlerOptions) {
  const { featuresSource, editor, resetIntersectionEditors } = options;
  featuresSource.current.clear();
  editor.reset();
  resetIntersectionEditors();
}

/**
 * Ripristina le geometrie dal valore originale FO (tag semantico): pulisce la
 * sorgente e ricarica le feature dal GeoJSON salvato, come fa `initDrawing` al
 * mount. Non tocca i campi sibling (catastale/custom/aree): vengono
 * ricalcolati client-side dal chiamante via `triggerIntersections`.
 */
function handleRestore(
  event: Extract<MapSubjectEvent, { type: 'restore' }>,
  options: IUseMapSubjectHandlerOptions,
) {
  const { featuresSource, editor, mapRef } = options;

  featuresSource.current.clear();
  featuresSource.current.addFeatures(
    convertGeometryItemsToFeatures(event.geometries),
  );
  editor.replace(event.geometries);
  fitViewToFeatures(mapRef.current, featuresSource.current);
}

/**
 * Gestisce l'aggiunta, la modifica e la rimozione delle geometrie
 * disegnate sulla mappa.
 */
export function useMapSubjectHandler(options: IUseMapSubjectHandlerOptions) {
  const {
    editor,
    featuresSource,
    widget,
    mapRef,
    getNextIndex,
    getNextIndexBatch,
    triggerIntersections,
    resetIntersectionEditors,
    t,
  } = options;

  const mapSubject = useRef<Subject<MapSubjectEvent>>(new Subject());

  useEffect(() => {
    const ctx: IUseMapSubjectHandlerOptions = {
      editor,
      featuresSource,
      widget,
      mapRef,
      getNextIndex,
      getNextIndexBatch,
      triggerIntersections,
      resetIntersectionEditors,
      t,
    };
    const listener = mapSubject.current.subscribe(event => {
      console.log(`[MapWidgetComponent] mapSubject`, event);
      switch (event?.type) {
        case 'add':
          return handleAdd(event, ctx);
        case 'upload':
          return handleUpload(event, ctx);
        case 'modify':
          return handleModify(event, ctx);
        case 'remove':
          return handleRemove(event, ctx);
        case 'removeId':
          return handleRemoveId(event, ctx);
        case 'reset':
          return handleReset(ctx);
        case 'restore':
          return handleRestore(event, ctx);
      }
    });

    return () => {
      listener.unsubscribe();
    };
  }, [
    editor,
    featuresSource,
    widget,
    mapRef,
    getNextIndex,
    getNextIndexBatch,
    triggerIntersections,
    resetIntersectionEditors,
    t,
  ]);

  return mapSubject;
}
